import AppLayout from "@/components/layout/AppLayout";
import { api } from "@/api/http";
import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Loader2, History } from "lucide-react";

type Mov = {
  id: string;
  tipo: string; // "entrada" | "saida"
  qtd: any;
  motivo?: string | null;
  refOsId?: string | null;
  createdAt: string;
};

type Resp = {
  peca: { id: string; codigo?: string | null; descricao: string };
  data: Mov[];
  meta: { page: number; limit: number; total: number; totalPages: number };
};

function toStr(v: any) {
  if (v === null || v === undefined) return "";
  return String(v);
}

function fmtDateTimeBR(iso: string) {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString("pt-BR");
}

function fmtQtd(v: any) {
  // Prisma Decimal às vezes vem como string, às vezes como objeto dependendo de como serializou
  const s = typeof v === "string" ? v : (v?.toString?.() ?? String(v ?? "0"));
  const n = Number(String(s).replace(",", "."));
  if (!Number.isFinite(n)) return s;
  return n.toLocaleString("pt-BR", { maximumFractionDigits: 3 });
}

function badgeTipo(tipo: string) {
  const t = (tipo || "").toLowerCase();
  if (t === "entrada") return "bg-emerald-100 text-emerald-800 border-emerald-200";
  if (t === "saida" || t === "saída") return "bg-rose-100 text-rose-800 border-rose-200";
  return "bg-slate-100 text-slate-800 border-slate-200";
}

function labelTipo(tipo: string) {
  const t = (tipo || "").toLowerCase();
  if (t === "entrada") return "Entrada";
  if (t === "saida" || t === "saída") return "Saída";
  return tipo || "—";
}

export default function PecaHistorico() {
  const { id } = useParams();

  const [loading, setLoading] = useState(true);
  const [peca, setPeca] = useState<{ id: string; codigo?: string | null; descricao: string } | null>(null);
  const [rows, setRows] = useState<Mov[]>([]);
  const [page, setPage] = useState(1);
  const [limit] = useState(30);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);

  const canPrev = page > 1;
  const canNext = page < totalPages;

  const title = useMemo(() => {
    if (!peca) return "Histórico de estoque";
    const code = peca.codigo ? ` (${peca.codigo})` : "";
    return `Histórico de estoque: ${peca.descricao}${code}`;
  }, [peca]);

  async function load(p = page) {
    if (!id) return;

    setLoading(true);
    try {
      const { data } = await api.get<Resp>(`/api/pecas/${id}/movimentos`, {
        params: { page: p, limit },
      });

      setPeca(data.peca);
      setRows(data.data || []);
      setTotalPages(data.meta?.totalPages ?? 1);
      setTotal(data.meta?.total ?? 0);
      setPage(data.meta?.page ?? p);
    } catch (err: any) {
      const msg = err?.response?.data?.error || err?.response?.data?.message || "Erro ao carregar histórico";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  return (
    <AppLayout>
      <div className="space-y-6">
        {/* Topo */}
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
              <History size={20} />
              Histórico de Movimentações
            </h1>
            <p className="text-sm text-slate-500">
              {peca ? (
                <>
                  {peca.descricao}
                  {peca.codigo ? ` • Código: ${peca.codigo}` : ""}
                </>
              ) : (
                "Visualize entradas e saídas do estoque"
              )}
            </p>
          </div>

          <div className="flex items-center gap-2">
            {id ? (
              <Link to={`/estoque/movimentar?pecaId=${id}`}>
                <Button className="gap-2">Movimentar estoque</Button>
              </Link>
            ) : null}

            <Link to="/pecas">
              <Button variant="outline" className="gap-2">
                <ArrowLeft size={16} />
                Voltar
              </Button>
            </Link>
          </div>
        </div>

        {/* Card */}
        <div className="bg-white rounded-xl shadow-sm">
          {/* Header do card */}
          <div className="p-5 border-b border-slate-100 flex items-center justify-between gap-3 flex-wrap">
            <div className="text-sm text-slate-600">
              <b>{title}</b>
              <span className="ml-2 text-slate-400">•</span>
              <span className="ml-2">{total.toLocaleString("pt-BR")} registro(s)</span>
            </div>

            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                disabled={!canPrev || loading}
                onClick={() => load(page - 1)}
              >
                Anterior
              </Button>
              <div className="text-sm text-slate-600">
                Página <b>{page}</b> / {totalPages}
              </div>
              <Button
                variant="outline"
                disabled={!canNext || loading}
                onClick={() => load(page + 1)}
              >
                Próxima
              </Button>
            </div>
          </div>

          {/* Conteúdo */}
          <div className="p-5">
            {loading ? (
              <div className="flex items-center justify-center py-16 text-slate-500 gap-2">
                <Loader2 className="animate-spin" size={18} />
                Carregando...
              </div>
            ) : rows.length === 0 ? (
              <div className="rounded-lg border border-slate-200 bg-slate-50 p-4 text-sm text-slate-600">
                Ainda não há movimentações registradas para essa peça.
                <div className="mt-2">
                  {id ? (
                    <Link to={`/estoque/movimentar?pecaId=${id}`}>
                      <Button className="mt-2">Fazer primeira movimentação</Button>
                    </Link>
                  ) : null}
                </div>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-slate-500 border-b">
                      <th className="py-2 pr-3">Data</th>
                      <th className="py-2 pr-3">Tipo</th>
                      <th className="py-2 pr-3">Qtd</th>
                      <th className="py-2 pr-3">Motivo</th>
                      <th className="py-2 pr-3">OS</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((m) => (
                      <tr key={m.id} className="border-b last:border-b-0">
                        <td className="py-3 pr-3 text-slate-700 whitespace-nowrap">
                          {fmtDateTimeBR(m.createdAt)}
                        </td>

                        <td className="py-3 pr-3">
                          <span
                            className={[
                              "inline-flex items-center rounded-full border px-2.5 py-1 text-xs font-medium",
                              badgeTipo(m.tipo),
                            ].join(" ")}
                          >
                            {labelTipo(m.tipo)}
                          </span>
                        </td>

                        <td className="py-3 pr-3 font-medium text-slate-800 whitespace-nowrap">
                          {m.tipo?.toLowerCase() === "entrada" ? "+" : "-"}
                          {fmtQtd(m.qtd)}
                        </td>

                        <td className="py-3 pr-3 text-slate-700">
                          {toStr(m.motivo) || "—"}
                        </td>

                        <td className="py-3 pr-3 text-slate-700 whitespace-nowrap">
                          {m.refOsId ? (
                            <span className="font-medium">{m.refOsId}</span>
                            // depois a gente troca isso por Link pra OS quando existir rota
                          ) : (
                            "—"
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Footer do card */}
          <div className="p-5 border-t border-slate-100 flex items-center justify-between gap-3 flex-wrap">
            <div className="text-xs text-slate-500">
              Dica: estoque atual muda via movimentação (e fica auditável aqui).
            </div>

            <div className="flex items-center gap-2">
              <Button variant="outline" disabled={!canPrev || loading} onClick={() => load(page - 1)}>
                Anterior
              </Button>
              <Button variant="outline" disabled={!canNext || loading} onClick={() => load(page + 1)}>
                Próxima
              </Button>
            </div>
          </div>
        </div>
      </div>
    </AppLayout>
  );
}
